import { Children, forwardRef, useCallback } from "react";
import { DisclosureContext } from "./DisclosureContext";
import { Keys, augmentElement, cssModule, isNil, mergeProps, resolveChildren, useControllableState, useEventCallback, useId, useMergedRefs } from "../../shared";
import { Text } from "../../typography";
import { useSlidingTransition } from "./useSlidingTransition";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DefaultElement = "div";
export function InnerDisclosure({
  as = DefaultElement,
  children,
  defaultOpen,
  forwardedRef,
  id,
  onOpenChange,
  open,
  ...rest
}) {
  const [isOpen, setIsOpen] = useControllableState(open, defaultOpen, false);
  const contentRef = useMergedRefs(forwardedRef);
  const toggle = useCallback(event => {
    setIsOpen(!isOpen);

    if (!isNil(onOpenChange)) {
      onOpenChange(event, !isOpen);
    }
  }, [isOpen, setIsOpen, onOpenChange]);
  const close = useCallback(event => {
    if (isOpen) {
      toggle(event);
    }
  }, [isOpen, toggle]);
  const [trigger, content] = Children.toArray(resolveChildren(children, {
    close
  }));

  if (isNil(trigger) || isNil(content)) {
    throw new Error("A disclosure component must have a trigger and a content element.");
  }

  const handleClick = useEventCallback(event => {
    event.preventDefault();
    toggle(event);
  });
  const handleKeyDown = useEventCallback(event => {
    switch (event.key) {
      case Keys.enter:
      case Keys.space:
        event.preventDefault();
        toggle(event);
        break;
    }
  }); // Hotfix for https://bugzilla.mozilla.org/show_bug.cgi?id=1487102

  const handleKeyUp = useEventCallback(event => {
    if (event.key === Keys.space) {
      event.preventDefault();
    }
  });
  const rootId = useId(id, "o-ui-disclosure");
  const contentId = `${rootId}-content`;
  const triggerMarkup = augmentElement(trigger, {
    "aria-controls": contentId,
    "aria-expanded": isOpen,
    onClick: handleClick,
    onKeyDown: handleKeyDown,
    onKeyUp: handleKeyUp
  });
  const contentMarkup = augmentElement(content, {
    "aria-hidden": !isOpen,
    className: "o-ui-disclosure-content-inner",
    id: contentId
  });
  const {
    transitionClasses,
    transitionProps
  } = useSlidingTransition(isOpen, contentRef);
  return /*#__PURE__*/_jsxs(DisclosureContext.Provider, {
    value: {
      close,
      isOpen
    },
    children: [triggerMarkup, /*#__PURE__*/_jsx(Text, { ...mergeProps(rest, {
        as,
        className: cssModule("o-ui-disclosure-content", transitionClasses),
        id: rootId,
        ref: contentRef
      }, transitionProps),
      children: contentMarkup
    })]
  });
}
InnerDisclosure.defaultElement = DefaultElement;
export const Disclosure = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/_jsx(InnerDisclosure, { ...props,
  forwardedRef: ref
}));